import { v4 } from "uuid";
import { Model } from "../models/model";
import { BasicTextStyledProperty } from "../documents/basic-text-styled-property";
import { TextStyledProperty } from "../documents/text-styled-property";
import { roundDecimalsAdvanced } from "../numbers";
import { Condition } from "../condition/condition";

export class TextLine {
	key:string = v4();
	PreText:string = "";
	Prop:string = "";
	PostText:string = "";
	PropRounding:number | null = null;
	Style:TextStyledProperty = new TextStyledProperty();
	PreTextStyle:BasicTextStyledProperty = new BasicTextStyledProperty();
	PropStyle:BasicTextStyledProperty = new BasicTextStyledProperty();
	PostTextStyle:BasicTextStyledProperty = new BasicTextStyledProperty();
	Conditions:Condition[] = [];

	public constructor(data?:any) {
		if(!data) return;
		this.PreText = data.PreText;
		this.Prop = data.Prop;
		this.PostText = data.PostText;
		this.PropRounding = data.PropRounding;
		this.Style = new TextStyledProperty(data.Style);
		this.PreTextStyle = new BasicTextStyledProperty(data.PreTextStyle);
		this.PropStyle = new BasicTextStyledProperty(data.PropStyle);
		this.PostTextStyle = new BasicTextStyledProperty(data.PostTextStyle);
		this.Conditions = (data.Conditions || []).map((c:any) => new Condition(c));
	}

	public getPreloads(model:Model):string[] {
		if (!this.Prop) return [];
		let field = model.getField(this.Prop);
		if (!field) return [];
		if (field.field.isComputed) return [];
		let result:string[] = [];
		if (field.field.dependencies) {
			result.push(...field.field.dependencies);
		}
		let parts = this.Prop.split(".");
		parts.pop();
		let path = parts.join(".");
		if (!path) return result;
		result.push(path);
		return result;
	}

	public getPropName(model:Model):string{
		if (this.Prop){
			let field = model.getField(this.Prop);
			if (field) {
				return field.getPropertyTranslation();
			}
		}
		return "";
	}

	public getAsText(model:Model):string{
		return `${this.PreText}${this.getPropName(model)}${this.PostText}`;
	}

	public getJSON():any{
		return {
			PreText: this.PreText,
			Prop: this.Prop,
			PostText: this.PostText,
			PropRounding: this.PropRounding,
			Style: this.Style.getJSON(),
			PreTextStyle: this.PreTextStyle.getJSON(),
			PropStyle: this.PropStyle.getJSON(),
			PostTextStyle: this.PostTextStyle.getJSON(),
			Conditions: this.Conditions.map(c => c.getJSON()),
		};
	}

	private computePropText(record:any, model:Model):string {
		if (!this.Prop) return "";
		let propParts = this.Prop.split(".");
		let propValue = record;
		for (let part of propParts){
			if (!propValue) return "";
			if (part.startsWith("calculated_field_")){
				let field = model.getField(this.Prop);
				if (!field){
					propValue = "";
				} else if (field.field.getValueFunc){
					propValue = field.field.getValueFunc(propValue);
				}
			}else{
				propValue = propValue[part];
			}
		}
		if (this.PropRounding != null && typeof(propValue) == "number") {
			propValue = roundDecimalsAdvanced(propValue, {decimals: this.PropRounding, decimalSeperator: ",", thousandSeperatorInterval: 3, thousandsSeperator: " "});
		}
		if (this.Prop.startsWith("ExtraFields.Time")){
			let date = new Date(propValue);
			if (!isNaN(date.getTime())) {
				const day = String(date.getDate()).padStart(2, "0");
				const month = String(date.getMonth() + 1).padStart(2, "0");
				const year = date.getFullYear();
				propValue = `${day}-${month}-${year}`;
			} else {
				propValue = "";
			}
		}
		return `${propValue}` || "";
	}

	private static setStyleOfEl(html:HTMLElement, s:any){
		for(let prop of Object.keys(s)){
			html.style.setProperty(prop, s[prop]);
		}
	}

	public computeTemplateData(){
		const today = new Date();
		const yyyy = today.getFullYear().toString();
		const yy   = yyyy.slice(-2);
		const mm   = String(today.getMonth() + 1).padStart(2, "0");
		const dd   = String(today.getDate()).padStart(2, "0");

		this.PreText = (this.PreText ?? "")
			.replace("{YYYY}", yyyy)
			.replace("{YY}", yy)
			.replace("{MM}", mm)
			.replace("{DD}", dd)
			.replace("{PrintDate}", yy + mm + dd);
	}


	public compute(record:any, model:Model):string{
		let preText = document.createElement("span");
		preText.innerText = this.PreText;
		TextLine.setStyleOfEl(preText, this.PreTextStyle.getStyle());
		let prop = document.createElement("span");
		prop.innerHTML = this.computePropText(record, model);
		TextLine.setStyleOfEl(prop, this.PropStyle.getStyle());
		let postText=  document.createElement("span");
		postText.innerText = this.PostText;
		TextLine.setStyleOfEl(postText, this.PostTextStyle.getStyle());
		let div = document.createElement("div");
		let style = this.Style.getStyle();
		delete style["font-size"];
		delete style["font-family"];
		TextLine.setStyleOfEl(div, this.Style.getStyle());
		div.appendChild(preText);
		div.appendChild(prop);
		div.appendChild(postText);
		return div.outerHTML;
	}
}
